import { JSX } from 'react';

import { styled, Tab, Tabs, TabsProps } from '@mui/material';
import { Link, useSearchParams } from 'react-router-dom';

import { UserRoleEnum } from '~enums';
import useAuthorize from '~hooks/useAuthorize';

export interface TabData {
	key: string;
	title: string;
	path: string;
	allowedRoles?: UserRoleEnum[];
	component: JSX.Element;
}

interface TabBarProps extends TabsProps {
	activeTabKey: string;
	tabs?: TabData[];
	/**
	 * Specify which url query params (search params) are perserved while navigating between tabs on the page.
	 */
	preservedTabSearchParams?: string[];
}

const TabBar = ({
	activeTabKey: activeTab,
	tabs,
	preservedTabSearchParams,
	...tabBarProps
}: TabBarProps) => {
	const authorize = useAuthorize();
	const query = useSearchParams();

	if (tabs == undefined) {
		return null;
	}
	const filteredTabs = tabs.filter((tab) => {
		return tab.allowedRoles != undefined ? authorize.authorize(tab.allowedRoles) : true;
	});

	if (filteredTabs.length < 2) {
		return <></>;
	}

	// Copy perserved search params
	let search = '';
	for (let i = 0; i < (preservedTabSearchParams?.length ?? 0); i++) {
		const param = preservedTabSearchParams?.[i] ?? '';
		const value = query[0].get(param) ?? '';
		if (value.length > 0) {
			search += i == 0 ? '?' : '&';
			search += `${param}=${value}`;
		}
	}

	return (
		<StyledTabs
			{...tabBarProps}
			variant='scrollable'
			value={activeTab}
			scrollButtons={false}
			allowScrollButtonsMobile
		>
			{filteredTabs.map((tab) => (
				<StyledTab
					key={tab.key}
					component={Link}
					value={tab.key}
					to={{
						pathname: tab.path,
						search: search,
					}}
					label={tab.title}
					relative='route'
				/>
			))}
		</StyledTabs>
	);
};

const StyledTab = styled(Tab)(({ theme }) => ({
	...theme.typography.labelSm,
	color: theme.palette.textQuaternary.main,
	textTransform: 'capitalize',
	'&.Mui-selected': {
		color: theme.palette.textPrimary.main,
	},
})) as typeof Tab;

const StyledTabs = styled(Tabs)(({ theme }) => ({
	overflow: 'unset',
	overflowX: 'auto',
	borderBottom: `1px solid ${theme.palette.borderSecondary.main}`,
	'& .MuiTabs-indicator': {
		backgroundColor: theme.palette.fgPrimary.main,
	},
}));

export default TabBar;
